// src/components/Notification.js
import React, { useEffect, useState } from "react";

const baseStyle = {
  position: "fixed",
  top: "1rem",
  right: "1rem",
  minWidth: "260px",
  padding: "1rem 1.5rem",
  borderRadius: "8px",
  color: "white",
  boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
  zIndex: 99999,
  fontWeight: 600,
  cursor: "pointer",
  transition: "opacity 0.35s ease, transform 0.35s ease",
  pointerEvents: "auto",
};

const types = {
  success: { backgroundColor: "#28a745" },
  error: { backgroundColor: "#dc3545" },
  warning: { backgroundColor: "#ffc107", color: "#212529", },
  info: { backgroundColor: "#007bff" },
};

export default function Notification({
  message,
  type = "success",
  onClose,
  duration = 5000, 
  action, // ✅ New prop for CTA action { label, onClick }
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!message) {
      setVisible(false);
      return;
    }

    // ✅ Show immediately when message appears
    setVisible(true);

    // ✅ Start fade-out animation 300ms before close
    const hideTimer = setTimeout(() => {
      setVisible(false);
    }, duration - 300);

    // ✅ Actually remove the notification after duration
    const closeTimer = setTimeout(() => {
      if (onClose) onClose();
    }, duration);

    return () => {
      clearTimeout(hideTimer);
      clearTimeout(closeTimer);
    };
  }, [message, duration, onClose]);

  if (!message) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        ...baseStyle,
        ...(types[type]),
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(-12px)",
        cursor: "default", // Changed from pointer to default to allow button interaction
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "1rem",
      }}
    >
      <span style={{ flex: 1 }}>{message}</span>
      {action && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            action.onClick();
            if (onClose) onClose();
          }}
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            border: "1px solid rgba(255, 255, 255, 0.4)",
            color: "white",
            padding: "4px 12px",
            borderRadius: "4px",
            fontSize: "0.85rem",
            fontWeight: "bold",
            cursor: "pointer",
            whiteSpace: "nowrap",
            transition: "background 0.2s",
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.3)"}
          onMouseOut={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)"}
        >
          {action.label}
        </button>
      )}
      {!action && (
        <span 
          style={{ cursor: "pointer", fontSize: "1.2rem", lineHeight: 1 }}
          onClick={(e) => {
            e.stopPropagation();
            if (onClose) onClose();
          }}
        >
          &times;
        </span>
      )}
    </div>
  );
}
